import { Component, Listen, Host, Prop, State, h, Element } from '@stencil/core';
import { handleAccessibility } from './slidefocus';

@Component({
  tag: 'visa-marquee-container',
  styleUrl: 'visa-marquee-container.scss',
  shadow: false,
})
export class VisaMarqueeContainer {
  @Prop() content: string;
  @Element() el: HTMLVisaMarqueeContainerElement;
  @State() activeIndex: number = 0;
  @State() progressRingValue: number = 0;
  @State() autoplayStatus: string = 'pause';
  @State() showPaddle: boolean = false;
  @State() marqueeHeight: number = 0;
  @State() oldActiveIndex = 0;
  @State() videoPlaying: boolean = false;

  private totalSlides: number = 0;
  private data: any;
  private autorotate: boolean;
  private progressRingInterval: any;
  private pauseVal: number;
  private startX: number;
  private threshold: number = 100;
  private pauseButtonClicked: boolean = false;
  private enablePaddle: boolean = false;
  private marqueeId: number = 0;

  private onNavigationButtonClick = (data: any) => {
    setTimeout(() => {
      this.changeSlide(data);
    }, 0);
  };

  connectedCallback() {
    this.data = JSON.parse(this.content);
    this.autorotate = this.data.disableTransition != 'true';
    this.totalSlides = this.data.items.length;
    if (this.data.items.length > 1 && this.autorotate) {
      this.enablePaddle = true;
      this.startProgressTimer();
    }
  }

  disconnectedCallback() {
    this.stopProgressTimer();
  }

  // START GENAI@GHCOPILOT
  componentDidLoad() {
    if (this.isInViewport(this.el)) {
      setTimeout(() => {
        this.renderHeight();
      }, 500);
    }

    const selectors = ['.vs-marquee[placeholder="true"]', '.vs-submarquee[placeholder="true"]'];
    selectors.forEach(selector => {
      const element = this.el.querySelector(selector);
      if (element) {
        element.remove();
      }
    });

    handleAccessibility(this);
  }

  private isInViewport(element) {
    const rect = element.getBoundingClientRect();
    return (
      (rect.top >= 0 && rect.left >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)) ||
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  private renderHeight = () => {
    if (this.data.verticalStack != 'vertical') {
      if (this.data.type == 'marquee') {
        if (!this.videoPlaying) {
          const marqueeItem = this.el.querySelector('.active .vs-marquee-item') as HTMLElement;
          this.marqueeHeight = marqueeItem.offsetHeight;
        }
      }
      if (this.data.type == 'submarquee') {
        const marqueeItem = this.el.querySelector('.active .vs-submarquee') as HTMLElement;
        this.marqueeHeight = marqueeItem.offsetHeight;
      }
    }
  };
  // END GENAI@GHCOPILOT

  // START GENAI@CHATGPT4
  componentDidUpdate() {
    if (this.oldActiveIndex !== this.activeIndex) {
      this.oldActiveIndex = this.activeIndex;
      handleAccessibility(this);
    }
  }
  // END GENAI@CHATGPT4

  // START GENAI@CHATGPT4
  componentDidRender() {
    this.renderHeight();
  }
  // END GENAI@CHATGPT4

  startProgressTimer(val: number = 0) {
    if (this.autorotate) {
      this.progressRingValue = val;
      this.progressRingInterval = setInterval(() => {
        this.progressRingValue = this.progressRingValue + 1;
        if (this.progressRingValue >= 100) {
          this.activeIndex = this.activeIndex + 1;
          if (this.activeIndex >= this.totalSlides) {
            this.activeIndex = 0;
          }
          this.progressRingValue = 0;
        }
      }, 50);
    }
  }

  stopProgressTimer() {
    clearInterval(this.progressRingInterval);
  }

  toggleProgressTimer() {
    if (this.autoplayStatus === 'pause') {
      this.pauseButtonClicked = true;
      this.pauseCarousel();
    } else {
      this.pauseButtonClicked = false;
      this.playCarousel();
    }
  }

  playCarousel() {
    const resumeFromVal = this.progressRingValue != 0 ? this.pauseVal : 0;
    this.startProgressTimer(resumeFromVal);
    this.autoplayStatus = 'pause';
  }

  pauseCarousel() {
    clearInterval(this.progressRingInterval);
    this.pauseVal = this.progressRingValue;
    this.autoplayStatus = 'play';
  }

  setActiveIndex(index: number) {
    this.activeIndex = index;
    this.progressRingValue = 0;
    this.stopProgressTimer();
    if (this.autoplayStatus === 'pause' && this.autorotate) {
      this.startProgressTimer();
    }
  }

  @Listen('pauseAutoplay')
  handlePauseAutoplay(event) {
    if (this.autorotate) {
      if (event.detail == 'play') {
        this.playCarousel();
      } else {
        this.pauseCarousel();
      }
    }
  }

  @Listen('videoPlaying')
  handleVideoPlaying(event) {
    if (this.data.type === 'marquee') {
      if (event.detail == 'true') {
        this.videoPlaying = true;
        this.enablePaddle = false;
        setTimeout(() => {
          const teaser = this.el.querySelector('.active .vs-marquee-item .vs-marquee-content-wrapper') as HTMLElement;
          if (window.innerWidth < 992) {
            this.marqueeHeight = window.innerHeight + teaser.offsetHeight;
          } else {
            this.marqueeHeight = window.innerHeight;
          }
        }, 2000);
      } else {
        this.videoPlaying = false;
        if (this.totalSlides > 1) {
          this.enablePaddle = true;
        }
      }
    }
  }

  handleTouchStart = (e: TouchEvent) => {
    this.startX = e.touches[0].clientX;
  };

  handleTouchEnd = (e: TouchEvent) => {
    const distance = this.startX - e.changedTouches[0].clientX;
    if (distance > this.threshold) {
      this.changeSlide('next');
    } else if (distance < -this.threshold) {
      this.changeSlide('prev');
    }
  };

  handleMouseEnter() {
    if (!this.videoPlaying) {
      this.showPaddle = true;
    }
    if (this.autorotate) {
      this.pauseCarousel();
    }
  }

  handleMouseLeave() {
    this.showPaddle = false;
    if (this.autorotate) {
      if (!this.pauseButtonClicked && !this.videoPlaying) {
        this.playCarousel();
      }
    }
  }

  private changeSlide(slideDir) {
    if (slideDir == 'prev') {
      this.activeIndex = this.activeIndex - 1;
      if (this.activeIndex < 0) {
        this.activeIndex = this.totalSlides - 1;
      }
    } else {
      this.activeIndex = this.activeIndex + 1;
      if (this.activeIndex >= this.totalSlides) {
        this.activeIndex = 0;
      }
    }
    this.progressRingValue = 0;
    this.stopProgressTimer();
    if (this.autoplayStatus === 'pause' && this.autorotate) {
      this.startProgressTimer();
    }
  }

  formatFootnoteText(value: string): string {
    if (value && !value.includes('vs-meta') && !value.includes('vs-legal')) {
      value = value.includes('vs-text') ? value.replace('vs-text', 'vs-text vs-legal') : '<div class="vs-text vs-legal">' + value + '</div>';
    }
    return value;
  }

  private renderHorizontalCarousel = () => {
    return this.data.items.map((item, index) => {
      return (
        /* START GENAI@GHCOPILOT */
        <li>
          <div class={this.activeIndex === index ? 'slide active' : 'slide'}>
            <vs-slide
              style={{ position: 'relative' }}
              data={item}
              type={this.data.type}
              onMouseEnter={() => this.handleMouseEnter()}
              onMouseLeave={() => this.handleMouseLeave()}
              accessibility={this.data.accessibility}
            ></vs-slide>
          </div>
        </li>
        /* START GENAI@GHCOPILOT */
      );
    });
  };

  render() {
    return (
      <Host>
        <div
          class={`vs-marquee-container ${this.data.items.length > 1 ? 'vs-mb-3' : ''}`}
          onTouchStart={event => this.handleTouchStart(event)}
          onTouchEnd={event => this.handleTouchEnd(event)}
        >
          {this.data.type === 'bubblemarquee' && (
            <visa-bubble-marquee data={this.data.items[0]} accessibility={this.data.accessibility}></visa-bubble-marquee>)}
          {this.data.verticalStack == 'vertical' &&
            this.data.items.map(item => {
              // START GENAI@CHATGPT4
              return (
                <div>
                  <vs-slide data={item} type={this.data.type} accessibility={this.data.accessibility}></vs-slide>
                  {item.footnoteText.length > 1 && (
                    <div class="vs-container-xl">
                      <div class="vs-col-xl-10 vs-mx-auto vs-px-0 vs-px-lg-2 vs-mt-1 vs-mb-3" innerHTML={this.formatFootnoteText(item.footnoteText)}></div>
                    </div>
                  )}
                </div>
              );
              // END GENAI@CHATGPT4
            })}
          {this.data.verticalStack != 'vertical' && (
            <div>
              <div class={`slides-wrapper-${this.data.type === 'submarquee' ? 'submarquee' : 'marquee'}`} style={{ height: `${this.marqueeHeight}px` }}>
                <ul>{this.renderHorizontalCarousel()}</ul>
                {this.enablePaddle && (
                  <vs-paddle-buttons parent-id={this.marqueeId} class={this.showPaddle ? 'vs-show' : ''} onPaddleClick={this.onNavigationButtonClick}></vs-paddle-buttons>
                )}
              </div>
              {this.data.items.length > 1 && !this.videoPlaying && (
                <vs-slide-indicator
                  totalSlides={this.totalSlides}
                  activeIndex={this.activeIndex}
                  value={this.progressRingValue}
                  onSlideClick={(event: CustomEvent) => this.setActiveIndex(event.detail)}
                  onPlayPause={() => this.toggleProgressTimer()}
                  autoplayStatus={this.autoplayStatus}
                  disableTransition={!this.autorotate}
                ></vs-slide-indicator>
              )}
              {this.data.items[this.activeIndex].footnoteText?.length > 0 && (
                <div class="vs-container-xl">
                  <div class="vs-col-xl-10 vs-mx-auto vs-px-0 vs-px-lg-2 vs-mt-1 vs-mb-3" innerHTML={this.formatFootnoteText(this.data.items[this.activeIndex].footnoteText)}></div>
                </div>
              )}
            </div>
          )}
        </div>
      </Host>
    );
  }
}
